import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { Role } from '../../../../../models/Role';
import { Department } from '../../../../../models/Department';
import { RoleAssignment } from '../../../../../models/RoleAssignment';
import { PermissionsService } from '../../../../../services/permissions.service';
import { Logger } from '../../../../../../../infrastructure/logging/logger';

@Component({
  selector: 'app-department-role-mapping',
  templateUrl: './department-role-mapping.component.html',
  styleUrls: ['./department-role-mapping.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatProgressSpinner
  ]
})
export class DepartmentRoleMappingComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  saving = new BehaviorSubject<boolean>(false);
  departments: Department[] = [];
  roles: Role[] = [];
  assignments: RoleAssignment[] = [];

  constructor(
    private permissionsService: PermissionsService,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.loadPage();
  }

  async loadPage() {
    try {
      this.loading.next(true);

      const [departments, roles, assignments] = await Promise.all([
        this.permissionsService.getCloudDepartments(),
        this.permissionsService.getRoles(),
        this.permissionsService.getRoleAssignments()
      ]);

      this.departments = departments;
      this.roles = roles;
      this.assignments = assignments.map(assignment => ({ ...assignment, departmentIds: assignment.departmentIds ?? [] }));
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Department Role Mappings', 'Error while retrieving Department Role Mappings');
    } finally {
      this.loading.next(false);
    }
  }

  isChecked(departmentId: string, roleId: string): boolean {
    const assignment = this.assignments.find(a => a.roleId === roleId);
    return !!assignment?.departmentIds.includes(departmentId);
  }

  isAllSelected(departmentId: string): boolean {
    return this.roles.length > 0 && this.roles.every(role => this.isChecked(departmentId, role.id));
  }

  isIndeterminate(departmentId: string): boolean {
    const checkedCount = this.roles.filter(role => this.isChecked(departmentId, role.id)).length;
    return checkedCount > 0 && checkedCount < this.roles.length;
  }

  onToggle(departmentId: string, roleId: string): void {
    const assignment = this.assignments.find(a => a.roleId === roleId);

    if (!assignment) {
      this.assignments.push({ id: '', roleId, departmentIds: [departmentId] });
      return;
    }

    const index = assignment.departmentIds.indexOf(departmentId);
    if (index >= 0) {
      assignment.departmentIds.splice(index, 1);
    } else {
      assignment.departmentIds.push(departmentId);
    }
  }

  onToggleSelectAll(departmentId: string): void {
    const selectAll = !this.isAllSelected(departmentId);

    this.roles.forEach(role => this.setChecked(departmentId, role.id, selectAll));
  }

  private setChecked(departmentId: string, roleId: string, checked: boolean): void {
    const assignment = this.assignments.find(a => a.roleId === roleId);

    if (!assignment) {
      if (checked) {
        this.assignments.push({ id: '', roleId, departmentIds: [departmentId] });
      }
      return;
    }

    const index = assignment.departmentIds.indexOf(departmentId);
    if (checked && index < 0) {
      assignment.departmentIds.push(departmentId);
    } else if (!checked && index >= 0) {
      assignment.departmentIds.splice(index, 1);
    }
  }

  async onSaveClick(): Promise<void> {
    try {
      this.saving.next(true);
      this.loading.next(true);
      this.assignments = await this.permissionsService.saveRoleAssignments(this.assignments);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while saving Department Role Mappings', 'Error while saving Department Role Mappings');
    } finally {
      this.loading.next(false);
      this.saving.next(false);
    }
  }
}
